
import React from 'react';
import { Button } from "@/components/ui/button";
import { useWalletConnection } from '@/wallet/hooks/useWalletConnection';
import { Wallet, Loader2 } from 'lucide-react';
import { isPhantomMobileApp } from '../utils/phantomUtils';

interface WalletButtonProps {
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

const WalletButton = ({
  size = "lg",
  className = "",
}: WalletButtonProps) => {
  const {
    connected,
    handleConnect,
    handleDisconnect,
    formattedAddress,
    connecting,
  } = useWalletConnection();

  // Check if we're in Phantom's mobile app
  const isMobilePhantom = isPhantomMobileApp();

  // Special event handlers for mobile
  const handleMobileDisconnect = (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent all default behaviors
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Mobile disconnect button clicked/tapped");
    
    // Call the disconnect handler
    handleDisconnect();
    
    // Return false to ensure event is fully cancelled
    return false;
  };

  if (connected) {
    return (
      <Button 
        // Use the mobile-specific handler for Phantom mobile
        onClick={isMobilePhantom ? handleMobileDisconnect : handleDisconnect}
        // For mobile, add touchstart handler for faster response
        onTouchStart={isMobilePhantom ? handleMobileDisconnect : undefined}
        className={`bg-gradient-to-r from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-glow ${className}`}
        size={size}
        // Add mobile-specific handling
        onTouchEnd={isMobilePhantom ? (e) => { e.preventDefault(); } : undefined}
        // Add data attribute to help with debugging
        data-mobile-phantom={isMobilePhantom ? "true" : "false"}
        // Ensure all click/touch behaviors don't trigger external navigation
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          isMobilePhantom ? handleMobileDisconnect(e) : handleDisconnect(e);
          return false;
        }}
      >
        <Wallet className="mr-2" />
        {formattedAddress}
      </Button>
    );
  }

  // Not connected - show connect button
  if (connecting) {
    return (
      <Button 
        disabled
        className={`bg-gradient-to-r from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-glow animate-pulse ${className}`}
        size={size}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Connecting...
      </Button>
    );
  }

  return (
    <Button 
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleConnect(e);
        return false;
      }}
      className={`bg-gradient-to-r from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-glow touch-none active:scale-95 ${className}`}
      size={size}
    >
      <Wallet className="mr-2" />
      Connect Wallet
    </Button>
  );
};

export default WalletButton;
